import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Button from 'devextreme-react/button';
import DataGrid, {
  Column,
  ColumnChooser,
  FilterPanel,
  FilterRow,
  GroupPanel,
  Grouping,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
  StateStoring,
} from 'devextreme-react/data-grid';
import LoadPanel from 'devextreme-react/load-panel';
import {
  Company,
  getCompanies,
  getStores,
} from '../../api/hub.service';
import './companies.scss';

export default function CompaniesPage() {
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState<Company>();

  const companies = useQuery({ queryKey: ['companies'], queryFn: getCompanies });
  const stores = useQuery({
    queryKey: ['stores', selectedCompany?.id],
    queryFn: () => getStores(selectedCompany!.id),
    enabled: Boolean(selectedCompany),
  });

  const selectedSummary = useMemo(
    () => selectedCompany ? `${selectedCompany.system} · Série ${selectedCompany.serialNumber}` : '',
    [selectedCompany]
  );

  return (
    <div className="page-body hub-page">
      <LoadPanel visible={companies.isLoading || stores.isLoading} />
      {!selectedCompany ? (
        <>
          <div className="hub-page-header">
            <div><span className="hub-eyebrow">Cadastros</span><h2>Empresas</h2><p>Catálogo oficial consultado em tempo real no PresenceTenant.</p></div>
            <span className="hub-status hub-status--positive">Sincronização ao vivo</span>
          </div>
          {companies.isError && <div className="hub-error">Não foi possível carregar as empresas.</div>}
          <div className="hub-grid-shell content-block">
            <DataGrid
              className="hub-grid"
              dataSource={companies.data ?? []}
              showBorders={false}
              showColumnLines={false}
              showRowLines
              rowAlternationEnabled
              hoverStateEnabled
              focusedRowEnabled
              allowColumnReordering
              allowColumnResizing
              columnAutoWidth
              columnHidingEnabled
              keyExpr="id"
              noDataText="Nenhuma empresa encontrada no PresenceTenant"
              onRowDblClick={(event) => setSelectedCompany(event.data)}
            >
              <StateStoring enabled type="localStorage" storageKey="presence-hub-companies-grid" />
              <SearchPanel visible width={310} placeholder="Pesquisar empresas..." />
              <GroupPanel visible emptyPanelText="Arraste uma coluna aqui para agrupar" />
              <Grouping contextMenuEnabled autoExpandAll={false} />
              <ColumnChooser enabled mode="select" title="Colunas da grade" />
              <HeaderFilter visible />
              <FilterRow visible applyFilter="auto" />
              <FilterPanel visible />
              <Paging defaultPageSize={20} />
              <Pager visible allowedPageSizes={[10, 20, 50, 100]} showPageSizeSelector showInfo showNavigationButtons />
              <Column dataField="id" caption="ID Tenant" width={110} dataType="number" />
              <Column dataField="system" caption="Sistema" width={100} />
              <Column dataField="serialNumber" caption="Série" width={110} />
              <Column dataField="masterSerialNumber" caption="Série master" width={130} />
              <Column dataField="name" caption="Nome" minWidth={240} />
              <Column
                caption="Ação"
                width={74}
                alignment="center"
                allowFiltering={false}
                allowGrouping={false}
                allowSorting={false}
                cellRender={({ data }) => <Button icon="chevronright" hint="Consultar lojas" stylingMode="text" onClick={() => setSelectedCompany(data)} />}
              />
            </DataGrid>
          </div>
          <div className="hub-grid-meta">
            <span>{(companies.data?.length ?? 0).toLocaleString('pt-BR')} empresas disponíveis</span>
            <span>Duplo clique ou use a ação para consultar as lojas</span>
          </div>
        </>
      ) : (
        <>
          <div className="hub-page-header">
            <div className="hub-title-with-back">
              <Button icon="back" hint="Voltar para empresas" stylingMode="outlined" onClick={() => setSelectedCompany(undefined)} />
              <div><span className="hub-eyebrow">Empresa selecionada</span><h2>{selectedCompany.name}</h2><p>{selectedSummary}</p></div>
            </div>
            <div className="hub-actions">
              <Button
                text="Configurar ElosGate"
                icon="card"
                type="default"
                onClick={() => navigate(`/provider-accounts?companyId=${selectedCompany.id}`)}
              />
            </div>
          </div>
          <section className="hub-section">
            <div className="hub-section-heading">
              <div><h3>Lojas da base operacional</h3><p>Somente lojas controladas e disponíveis para configuração.</p></div>
              <span className="hub-status hub-status--neutral">{stores.data?.length ?? 0} lojas</span>
            </div>
            {stores.isError && <div className="hub-error">Não foi possível consultar as lojas. Verifique o roteamento da empresa no PresenceTenant.</div>}
            <div className="hub-grid-shell">
              <DataGrid
                className="hub-grid"
                dataSource={stores.data ?? []}
                showBorders={false}
                showColumnLines={false}
                showRowLines
                rowAlternationEnabled
                hoverStateEnabled
                allowColumnReordering
                allowColumnResizing
                columnAutoWidth
                keyExpr="id"
                noDataText="Nenhuma loja controlada encontrada"
              >
                <SearchPanel visible width={280} placeholder="Pesquisar lojas..." />
                <GroupPanel visible emptyPanelText="Arraste uma coluna aqui para agrupar" />
                <Grouping contextMenuEnabled autoExpandAll={false} />
                <ColumnChooser enabled mode="select" title="Colunas da grade" />
                <HeaderFilter visible />
                <FilterRow visible applyFilter="auto" />
                <FilterPanel visible />
                <Paging defaultPageSize={20} />
                <Pager visible allowedPageSizes={[10, 20, 50]} showPageSizeSelector showInfo />
                <Column dataField="code" caption="Código" width={130} />
                <Column dataField="name" caption="Nome" />
                <Column
                  dataField="active"
                  caption="Status"
                  width={120}
                  dataType="boolean"
                  cellRender={({ value }) => <span className={`hub-status ${value ? 'hub-status--positive' : 'hub-status--neutral'}`}>{value ? 'Ativa' : 'Inativa'}</span>}
                />
              </DataGrid>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
